import { useState, useRef, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import ArticleIcon from "@mui/icons-material/Article";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import {
  useBulkTagConversations,
  useGetConversation,
  useListConversations,
  useListTags,
  useDeleteConversation,
  useUpdateConversation,
} from "../../api/generated/generated";
import type {
  ConversationDetail,
  ConversationResponse,
  TagCount,
} from "../../api/generated";
import { useDebounce } from "../../hooks/useDebounce";
import { TOPBAR_HEIGHT } from "../../theme";
import { ConversationView } from "./components/ConversationView";
import { HistoryDrawer } from "./components/HistoryDrawer";
import { NewConversationDialog } from "./components/NewConversationDialog";
import { NewConversationForm } from "./components/NewConversationForm";
import { MODE_META, type ConversationMode } from "./components/modeMeta";

const MODE_FILTERS = [
  { label: "All", value: undefined },
  { label: "Pre-Call", value: "pre_call" },
  { label: "Post-Call", value: "post_call" },
] as const;

export function ChatPage() {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [filterTab, setFilterTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  // Open history drawer when navigated with showHistory state
  useEffect(() => {
    if (location.state?.showHistory) {
      setHistoryOpen(true);
      // Clear the state so it doesn't re-trigger on re-renders
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, location.pathname, navigate]);
  const [scorePanelOpen, setScorePanelOpen] = useState(false);
  const [briefingPanelOpen, setBriefingPanelOpen] = useState(false);

  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Search and tag filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTagFilters, setActiveTagFilters] = useState<string[]>([]);
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Bulk tagging state
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const modeFilter = MODE_FILTERS[filterTab]?.value;
  const conversations = useListConversations({
    mode: modeFilter,
    search: debouncedSearch || undefined,
    tags: activeTagFilters.length ? activeTagFilters.join(",") : undefined,
  });
  const deleteConversation = useDeleteConversation();
  const updateConversation = useUpdateConversation();
  const bulkTag = useBulkTagConversations();
  const { data: conversationData } = useGetConversation(conversationId ?? "", {
    query: { enabled: !!conversationId },
  });
  const conversationDetail = conversationData?.data as
    | ConversationDetail
    | undefined;
  const conversation = conversationDetail?.conversation;

  // Org tags for autocomplete
  const orgTags = useListTags();
  const tagSuggestions: TagCount[] =
    (orgTags.data?.data as TagCount[] | undefined) ?? [];

  // Focus title input when editing
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Reset editing when conversation changes
  useEffect(() => {
    setIsEditingTitle(false);
  }, [conversationId]);

  const handleSelect = (id: string) => {
    navigate(`/chat/${id}`);
    setHistoryOpen(false);
  };

  const handleDelete = (id: string) => {
    deleteConversation.mutate(
      { conversationId: id },
      {
        onSuccess: () => {
          if (conversationId === id) {
            navigate("/chat");
          }
        },
      },
    );
  };

  const handleRename = (id: string, title: string) => {
    updateConversation.mutate({ conversationId: id, data: { title } });
  };

  const invalidateTagQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["/api/v1/conversations"] });
    queryClient.invalidateQueries({ queryKey: ["/api/v1/tags"] });
  }, [queryClient]);

  const handleAddTag = (id: string, tag: string) => {
    updateConversation.mutate(
      { conversationId: id, data: { add_tags: [tag] } },
      { onSuccess: invalidateTagQueries },
    );
  };

  const handleRemoveTag = (id: string, tag: string) => {
    updateConversation.mutate(
      { conversationId: id, data: { remove_tags: [tag] } },
      { onSuccess: invalidateTagQueries },
    );
  };

  const handleTagFilterAdd = useCallback((tag: string) => {
    setActiveTagFilters((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
  }, []);

  const handleTagFilterRemove = useCallback((tag: string) => {
    setActiveTagFilters((prev) => prev.filter((t) => t !== tag));
  }, []);

  const handleTagFilterClear = useCallback(() => {
    setActiveTagFilters([]);
  }, []);

  const handleBulkModeToggle = () => {
    setBulkMode((prev) => !prev);
    setSelectedIds(new Set());
  };

  const handleBulkToggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleBulkApply = (tags: string[]) => {
    bulkTag.mutate(
      {
        data: { conversation_ids: Array.from(selectedIds), add_tags: tags },
      },
      { onSuccess: invalidateTagQueries },
    );
    setBulkMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkCancel = () => {
    setBulkMode(false);
    setSelectedIds(new Set());
  };

  const handleCreated = (newId: string) => {
    setDialogOpen(false);
    navigate(`/chat/${newId}`);
  };

  const handleStartEdit = () => {
    if (conversation) {
      setEditTitle(conversation.title);
      setIsEditingTitle(true);
    }
  };

  const handleSaveTitle = () => {
    const trimmed = editTitle.trim();
    if (!trimmed || !conversationId || trimmed === conversation?.title) {
      setIsEditingTitle(false);
      return;
    }
    updateConversation.mutate(
      { conversationId, data: { title: trimmed } },
      {
        onSuccess: () => {
          setIsEditingTitle(false);
        },
      },
    );
  };

  const handleCancelEdit = () => {
    setIsEditingTitle(false);
  };

  const showScoresButton = conversation?.mode === "post_call";
  const showBriefingButton = conversation?.mode === "pre_call";
  const modeMeta = conversation
    ? MODE_META[conversation.mode as ConversationMode]
    : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
        m: -3,
        overflow: "hidden",
      }}
    >
      {/* Top action bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
            flex: 1,
          }}
        >
          {isEditingTitle ? (
            <TextField
              inputRef={titleInputRef}
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onBlur={handleSaveTitle}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveTitle();
                if (e.key === "Escape") handleCancelEdit();
              }}
              size="small"
              variant="standard"
              sx={{
                "& .MuiInput-input": { fontSize: "1.25rem", fontWeight: 500 },
              }}
              slotProps={{
                htmlInput: { maxLength: 200 }
              }}
            />
          ) : (
            <>
              <Typography variant="h6" noWrap>
                {conversation ? conversation.title : "AI Coach"}
              </Typography>
              {conversation && (
                <IconButton
                  size="small"
                  onClick={handleStartEdit}
                  sx={{ ml: 0.5 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          {showBriefingButton && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArticleIcon />}
              onClick={() => setBriefingPanelOpen(true)}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                Briefing
              </Box>
            </Button>
          )}
          {showScoresButton && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AssessmentIcon />}
              onClick={() => setScorePanelOpen(true)}
            >
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                Scores
              </Box>
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              New Chat
            </Box>
          </Button>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => setHistoryOpen(true)}
          >
            <Box
              component="span"
              sx={{ display: { xs: "none", sm: "inline" } }}
            >
              History
            </Box>
          </Button>
        </Box>
      </Box>
      {/* Chat bubble card */}
      <Box sx={{ flex: 1, overflow: "hidden", px: 3, pb: 2 }}>
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {/* Card header — avatar + name + mode chip */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1.25,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "grey.50",
            }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>
              <SmartToyOutlinedIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Typography variant="subtitle1" sx={{
              fontWeight: 600
            }}>
              Sales Coach
            </Typography>
            {modeMeta && (
              <Chip
                label={modeMeta.label}
                size="small"
                color={modeMeta.color}
                icon={<modeMeta.Icon />}
              />
            )}
          </Box>

          {/* Chat content */}
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            {conversationId ? (
              <ConversationView
                conversationId={conversationId}
                scorePanelOpen={scorePanelOpen}
                onScorePanelClose={() => setScorePanelOpen(false)}
                briefingPanelOpen={briefingPanelOpen}
                onBriefingPanelClose={() => setBriefingPanelOpen(false)}
              />
            ) : (
              <Box sx={{ height: "100%", overflow: "auto", p: 3 }}>
                <Box sx={{ maxWidth: 600, mx: "auto" }}>
                  <Typography variant="h5" sx={{
                    fontWeight: 600
                  }}>
                    AI Coach
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      mb: 3
                    }}>
                    Choose a tool to get started — research an upcoming call, or
                    analyze a completed one.
                  </Typography>
                  <NewConversationForm onCreated={handleCreated} />
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
      {/* History drawer */}
      <HistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        conversations={
          conversations.data?.data as ConversationResponse[] | undefined
        }
        isLoading={conversations.isLoading}
        selectedId={conversationId ?? null}
        onSelect={handleSelect}
        onDelete={handleDelete}
        onRename={handleRename}
        onAddTag={handleAddTag}
        onRemoveTag={handleRemoveTag}
        filterTab={filterTab}
        onFilterChange={setFilterTab}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        activeTagFilters={activeTagFilters}
        onTagFilterAdd={handleTagFilterAdd}
        onTagFilterRemove={handleTagFilterRemove}
        onTagFilterClear={handleTagFilterClear}
        tagSuggestions={tagSuggestions}
        bulkMode={bulkMode}
        onBulkModeToggle={handleBulkModeToggle}
        selectedIds={selectedIds}
        onBulkToggle={handleBulkToggle}
        onBulkApply={handleBulkApply}
        onBulkCancel={handleBulkCancel}
      />
      {/* New conversation dialog */}
      <NewConversationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={handleCreated}
      />
    </Box>
  );
}
